import { apiUrl } from '../../../config';
import logger from '../../Logger';
import { expireLocalSession } from '../../SessionExpiry';

const SERVICE_UNAVAILABLE_RECONNECT_DELAY = 30000;

/**
 * SSEAdapter
 *
 * Server-Sent Events implementation of the IListenerAdapter interface.
 * Opens ONE authenticated fetch-based SSE connection (EventSource does not
 * support custom headers) and dispatches events to registered callbacks by
 * matching the `action` field in each event payload.
 *
 * Drop-in replacement for FirestoreAdapter — same public API.
 */
class SSEAdapter {
  constructor() {
    // listenerId → { action, onData, onError }
    this._listeners = new Map();
    // action → Set<listenerId>   (one action can have multiple registered callbacks)
    this._actionIndex = new Map();
    this._abortController = null;
    this._reconnectTimer = null;
    this._reconnectDelay = 500;  // ms, doubles on failure up to MAX_DELAY
    this._maxDelay = 10000;
    this._running = false;
    this._hasConnectedOnce = false;
    this._lastEventIdKey = null;
    this._seenEventIds = new Set();
  }

  // -----------------------------------------------------------------------
  // IListenerAdapter interface
  // -----------------------------------------------------------------------

  async initialize({ orgId, userId }) {
    this._lastEventIdKey = `photon_sse_last_event_id:${orgId}:${userId}`;
    this._running = true;
    this._connect();
  }

  /**
   * Register a callback for a specific action.
   *
   * @param {string} path   - Ignored for SSE (all events arrive on one channel)
   * @param {string} action - Event action name (e.g., 'ADD_COLLABORATOR')
   * @param {Function} onData  - Called with event data when action fires
   * @param {Function} onError - Called on stream error
   * @returns {string} Unique listener ID
   */
  createListener(path, action, onData, onError) {
    const listenerId = `${path}/${action}`;

    if (this._listeners.has(listenerId)) {
      return listenerId;
    }

    this._listeners.set(listenerId, { path, action, onData, onError });

    if (!this._actionIndex.has(action)) {
      this._actionIndex.set(action, new Set());
    }
    this._actionIndex.get(action).add(listenerId);

    return listenerId;
  }

  removeListener(listenerId) {
    const entry = this._listeners.get(listenerId);
    if (!entry) return;

    const { action } = entry;
    this._listeners.delete(listenerId);

    const ids = this._actionIndex.get(action);
    if (ids) {
      ids.delete(listenerId);
      if (ids.size === 0) this._actionIndex.delete(action);
    }
  }

  async cleanup() {
    this._running = false;
    clearTimeout(this._reconnectTimer);
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._listeners.clear();
    this._actionIndex.clear();
    logger.info('SSEAdapter', 'Cleaned up SSE connection and all listeners');
  }

  getActiveListenerCount() {
    return this._listeners.size;
  }

  // -----------------------------------------------------------------------
  // Internal — SSE connection management
  // -----------------------------------------------------------------------

  _connect() {
    if (!this._running) return;

    const sessionToken = localStorage.getItem('session_token');
    if (!sessionToken) {
      logger.warn('SSEAdapter', 'No session token — skipping SSE connect');
      return;
    }

    // Abort any existing fetch before opening a new one — prevents duplicate
    // connections from accumulating during React StrictMode double-mount or
    // rapid reconnect cycles.
    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();

    const lastEventId = localStorage.getItem(this._lastEventIdKey);
    fetch(`${apiUrl}/events/stream`, {
      headers: {
        'X-Session-Token': sessionToken,
        ...(lastEventId ? { 'Last-Event-ID': lastEventId } : {}),
      },
      signal: this._abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 401) {
            this._running = false;
            clearTimeout(this._reconnectTimer);
            expireLocalSession(sessionToken, {
              reason: 'sse_401',
              source: 'sse_adapter',
            });
            logger.warn('SSEAdapter', 'SSE authentication rejected with HTTP 401; connection stopped');
            return null;
          }
          if (response.status === 403) {
            this._running = false;
            clearTimeout(this._reconnectTimer);
            logger.warn('SSEAdapter', 'SSE authorization rejected with HTTP 403; connection stopped');
            return null;
          }
          if (response.status === 503) {
            this._reconnectDelay = SERVICE_UNAVAILABLE_RECONNECT_DELAY;
          }
          throw new Error(`SSE HTTP ${response.status}`);
        }
        this._reconnectDelay = 500;  // reset on successful connect
        if (this._hasConnectedOnce) {
          // Notify listeners that connection was restored so they can refresh stale cache
          const reconnectData = { timestamp: new Date().toISOString() };
          const listenerIds = this._actionIndex.get('SSE_RECONNECTED');
          if (listenerIds) {
            for (const listenerId of listenerIds) {
              const entry = this._listeners.get(listenerId);
              if (entry) {
                try { entry.onData(reconnectData); } catch (_) { /* ignore */ }
              }
            }
          }
        }
        this._hasConnectedOnce = true;
        return this._readStream(response.body.getReader());
      })
      .catch((err) => {
        if (!this._running) return;
        if (err.name === 'AbortError') return;
        logger.warn('SSEAdapter', `SSE stream error — reconnecting in ${this._reconnectDelay}ms`, { error: err.message });
        this._scheduleReconnect();
      });
  }

  async _readStream(reader) {
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by double newlines
        const events = buffer.split('\n\n');
        buffer = events.pop(); // keep incomplete last chunk

        for (const chunk of events) {
          this._processChunk(chunk);
        }
      }
    } catch (err) {
      if (!this._running || err.name === 'AbortError') return;
      logger.warn('SSEAdapter', 'Stream read error', { error: err.message });
    } finally {
      if (this._running) {
        this._scheduleReconnect();
      }
    }
  }

  _processChunk(chunk) {
    // Ignore heartbeat comments (": heartbeat")
    if (chunk.trim().startsWith(':')) return;

    const dataLine = chunk
      .split('\n')
      .find((line) => line.startsWith('data:'));

    if (!dataLine) return;

    const raw = dataLine.slice('data:'.length).trim();
    if (!raw) return;

    let envelope;
    try {
      envelope = JSON.parse(raw);
    } catch (err) {
      logger.warn('SSEAdapter', 'Failed to parse SSE event', { raw });
      return;
    }

    const action = envelope?.action;
    if (!action) return;

    const idLine = chunk
      .split('\n')
      .find((line) => line.startsWith('id:'));
    const eventId = envelope?.event_id || idLine?.slice('id:'.length).trim();
    if (eventId && this._seenEventIds.has(eventId)) return;

    const eventData = envelope?.data && typeof envelope.data === 'object'
      ? {
          ...envelope.data,
          org_id: envelope.org_id,
          chat_id: envelope.chat_id,
          space_id: envelope.space_id,
          owner_id: envelope.owner_id,
          event_id: envelope.event_id,
        }
      : envelope;

    const listenerIds = this._actionIndex.get(action);
    if (!listenerIds || listenerIds.size === 0) return;

    for (const listenerId of listenerIds) {
      const entry = this._listeners.get(listenerId);
      if (entry && this._matchesScope(entry.path, envelope)) {
        try {
          entry.onData(eventData);
        } catch (err) {
          logger.error('SSEAdapter', `Handler error for ${listenerId}`, { error: err.message });
          entry.onError(err);
        }
      }
    }

    if (eventId) {
      this._seenEventIds.add(eventId);
      if (this._seenEventIds.size > 1000) {
        this._seenEventIds.delete(this._seenEventIds.values().next().value);
      }
      localStorage.setItem(this._lastEventIdKey, eventId);
    }
  }

  _matchesScope(path, envelope) {
    if (envelope?.chat_id) {
      return path.includes(`/chats/${envelope.chat_id}/`);
    }
    if (envelope?.space_id) {
      return path.includes(`/spaces/${envelope.space_id}/`);
    }
    return !path.includes('/chats/') && !path.includes('/spaces/');
  }

  _scheduleReconnect() {
    if (!this._running) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectDelay = Math.min(this._reconnectDelay * 2, this._maxDelay);
      this._connect();
    }, this._reconnectDelay);
  }
}

export default SSEAdapter;
